import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { thumbUrl } from "@/lib/api";
import { useCachedGet } from "@/lib/cache";
import LazyImage from "@/components/LazyImage";
import { etiquetaPaso } from "@/lib/pasoLabel";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const COLUMNS = [
  { key: "Desarrollo · Horma", label: "Desarrollo · Horma", color: "#a855f7" },
  { key: "Diseño", label: "Diseño", color: "#8b5cf6" },
  { key: "Prototipo", label: "Desarrollo · Prototipo", color: "#3b82f6" },
  { key: "Muestra", label: "Desarrollo · Muestra", color: "#f59e0b" },
  { key: "Cierre", label: "Desarrollo · Cierre", color: "#0ea5e9" },
  { key: "Alta de Producto", label: "Alta de Producto · Por liberar", color: "#ec4899" },
  { key: "Liberado", label: "Liberado", color: "#14b8a6" },
  { key: "Depurado", label: "Depurados", color: "#64748b" },
];

export default function Kanban() {
  const navigate = useNavigate();
  const { data: modelos } = useCachedGet("/modelos");
  const { data: coleccionesData } = useCachedGet("/colecciones");
  const colecciones = coleccionesData || [];
  const [colFilter, setColFilter] = useState("all");

  const grouped = useMemo(() => {
    const g = {};
    COLUMNS.forEach((c) => (g[c.key] = []));
    (modelos || [])
      .filter((m) => colFilter === "all" || m.coleccion_id === colFilter)
      .forEach((m) => {
        if (m?.depurado) { g["Depurado"].push(m); return; }
        if (m?.estatus?.liberado) { g["Liberado"].push(m); return; }
        const key = m?.estatus?.siguiente_paso?.fase;
        (g[key] || g["Desarrollo · Horma"]).push(m);
      });
    g["Depurado"].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" }));
    g["Liberado"].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" }));
    return g;
  }, [modelos, colFilter]);

  return (
    <div className="p-6 lg:p-8 h-full flex flex-col">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Kanban</h1>
          <p className="text-slate-500 mt-1">Las tarjetas avanzan de columna al completar los pasos de cada fase.</p>
        </div>
        <Select value={colFilter} onValueChange={setColFilter}>
          <SelectTrigger className="w-[220px] bg-white" data-testid="kanban-coleccion-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las colecciones</SelectItem>
            {colecciones.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!modelos ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : (
        <div className="flex gap-4 overflow-x-auto thin-scroll pb-4">
          {COLUMNS.map((col) => (
            <div key={col.key} className="min-w-[290px] w-[290px] shrink-0" data-testid={`kanban-col-${col.key}`}>
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: col.color }} />
                <span className="text-sm font-bold text-slate-700">{col.label}</span>
                <span className="ml-auto text-xs font-semibold text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">{grouped[col.key].length}</span>
              </div>
              <div className="space-y-3">
                {grouped[col.key].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => navigate(`/modelos/${m.id}`)}
                    data-testid={`kanban-card-${m.id}`}
                    className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:-translate-y-[1px] transition-all"
                  >
                    <div className="flex items-start justify-between gap-2">
                      {m.multimedia.boceto_url || m.multimedia.boceto_file_id ? (
                        <LazyImage src={m.multimedia.boceto_file_id ? thumbUrl(m.multimedia.boceto_file_id) : m.multimedia.boceto_url} alt="" containerClassName="h-12 w-12 rounded-lg bg-slate-50 shrink-0" imgClassName="object-cover" />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-slate-100 shrink-0" />
                      )}
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{m.organizacion.categoria || "—"}</span>
                    </div>
                    <div className="font-semibold text-slate-800 mt-3 text-sm truncate">{m.nombre}</div>
                    <div className="flex items-center gap-2 mt-0.5 min-w-0">
                      <span className="text-xs text-slate-400 font-mono shrink-0">{m.sku}</span>
                      <span className="text-[11px] text-slate-500 truncate" data-testid={`kanban-sigue-${m.id}`}>
                        {m.depurado ? "· Depurado" : m.estatus?.liberado ? "· Liberado" : m.estatus?.siguiente_paso ? `· Sigue: ${etiquetaPaso(m.estatus.siguiente_paso, m.pasos)}` : "· Sin iniciar"}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full" style={{ width: `${m.depurado ? 100 : m.estatus.porcentaje}%`, background: col.color }} />
                      </div>
                      <span className="text-xs font-semibold tabular-nums text-slate-500">{m.depurado ? "—" : `${m.estatus.porcentaje}%`}</span>
                    </div>
                  </button>
                ))}
                {grouped[col.key].length === 0 && <div className="text-xs text-slate-300 text-center py-6 border border-dashed border-slate-200 rounded-xl">Vacío</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
